import {
  DashboardState,
  ESdChangeReason,
  EUserAccessPart,
  IDevice,
  type IScreenCanvasSize,
  getReportCanvasSize
} from "@kuika/kuika-cl-model";
import { IKuikaAgentProp } from "@kuika/kuika-react-cl-agent";
import * as React from "react";
import ReactResizeDetector from "react-resize-detector";
import deviceListJson from "../../../shared/data/device-list.json";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";

type CustomElement<T> = Partial<T & React.DOMAttributes<T> & { children: any }>;

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ["kuika-agent"]: CustomElement<IKuikaAgentProp>;
    }
  }
}

export interface ICanvasFrameState {}

export interface ICanvasFrameProps {
  zoomScale: number;
}

class CanvasFrame extends PureSdComponent<ICanvasFrameProps & IWithSDProps, ICanvasFrameState> {
  deviceList: IDevice[] = deviceListJson as unknown as IDevice[];

  constructor(props: ICanvasFrameProps & IWithSDProps) {
    super(props);
  }

  private wrapperObserver: ResizeObserver | undefined;

  private observedWrapper: HTMLElement | undefined;

  private observedWrapperWidth: number | undefined;

  componentDidMount() {
    this.setCanvasFrameEditability();
    window.addEventListener("resize", this.handleResize);
    this.observeIframeWrapper();
  }

  componentDidUpdate(prevProps) {
    this.waitAndResize();
    this.observeIframeWrapper();

    if (this.props.sd?.project?.projectAccess?.length !== prevProps.sd.project?.projectAccess?.length) {
      this.setCanvasFrameEditability();
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener("resize", this.handleResize);
    this.wrapperObserver?.disconnect();
    this.wrapperObserver = undefined;
    this.observedWrapper = undefined;
    this.observedWrapperWidth = undefined;
    const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    if (activeCanvasIFrame) {
      (activeCanvasIFrame.contentWindow as any)?.kuika?.activeKuikaReactInstance?.setState({
        changeReason: ESdChangeReason.OtherSetStates,
        projectTempStyles: {},
        project: {},
        screenDesign: {},
        dashboardState: {}
      });

      activeCanvasIFrame.dataset.visible = "false";
    }
  }

  /**
   * Watches the element `onResize` actually measures.
   *
   * The `ReactResizeDetector` below wraps an invisible placeholder whose width is pinned to the
   * page in report mode, so it stops changing exactly when the frame needs re-placing: the page
   * grew, the design area and the wrapper grew with it, and nothing told the frame. Observing
   * `.kuika_iframeWrapper` closes that gap for every cause — page size, panel width, window.
   *
   * Re-targets when the wrapper is replaced (the canvas is remounted on a tool switch), because
   * an observer left on a detached node reports nothing and the gap would quietly come back.
   */
  observeIframeWrapper = (): void => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const wrapper: HTMLElement | undefined = document.getElementsByClassName("kuika_iframeWrapper")[0] as
      | HTMLElement
      | undefined;
    if (!wrapper || wrapper === this.observedWrapper) {
      return;
    }
    this.wrapperObserver?.disconnect();
    this.wrapperObserver = new ResizeObserver(this.handleWrapperResize);
    this.wrapperObserver.observe(wrapper);
    this.observedWrapper = wrapper;
    this.observedWrapperWidth = undefined;
  };

  /**
   * Width only. `onResize` writes the wrapper's HEIGHT, so reacting to every resize would make
   * each pass schedule one more: it settles today because the second pass writes the same value,
   * but it is a loop by construction and the browser reports it as one the moment a height ever
   * depends on a measured width.
   */
  handleWrapperResize = (entries: ResizeObserverEntry[]): void => {
    const width: number | undefined = entries[0]?.contentRect.width;
    if (width !== undefined && width === this.observedWrapperWidth) {
      return;
    }
    this.observedWrapperWidth = width;
    this.waitAndResize();
  };

  setCanvasFrameEditability = () => {
    const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const hasUserAccessEditComponents = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.EditComponent,
      this.props.sd.project?.projectAccess
    );
    if (activeCanvasIFrame && activeCanvasIFrame.style) {
      activeCanvasIFrame.style.pointerEvents = hasUserAccessEditComponents ? "auto" : "none";
    }
  };

  handleResize = () => {
    this.waitAndResize();
  };

  waitAndResize = (event?: any) => {
    const display =
      this.props.sd.isProjectLoaded === true &&
      this.props.sd.dashboardState !== DashboardState.dataDiagramScheme &&
      this.props.sd.dashboardState !== DashboardState.workFlow &&
      this.props.sd.dashboardState !== DashboardState.configManager &&
      this.props.sd.dashboardState !== DashboardState.notificationTemplate &&
      this.props.sd.dashboardState !== DashboardState.runSettings &&
      this.props.sd.dashboardState !== DashboardState.branching;

    KMainFunctions.waitAndResize(display, this.props.sd.toolbarIsHidden, this.props.sd, this.getZoomScale());
  };

  getZoomScale = () => {
    if (
      this.props.sd.dashboardState !== DashboardState.design &&
      this.props.sd.dashboardState !== DashboardState.reportDesigner
    ) {
      return 1;
    }
    return this.props.zoomScale;
  };

  getDesignFrameWidth = () => {
    if (this.getZoomScale() > 1) {
      return `calc(100% / ${this.getZoomScale()})`;
    }
    return "100%";
  };

  getDesignFrameWidthReport = (): string => {
    const screen: IScreenCanvasSize | undefined = this.props.sd.project?.screenCanvasSizes?.screens?.find(
      (entry: IScreenCanvasSize): boolean => entry.id === this.props.sd.sdId
    );
    return `${getReportCanvasSize(screen).width}px`;
  };

  getDesignFrameHeight = () => {
    let margin = 0;
    if (this.getZoomScale() !== 1) {
      margin /= this.getZoomScale();
    }
    return `calc(${100 / this.getZoomScale()}% - ${margin}px)`;
  };

  getFrameStyles = () => {
    if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
      return {
        transform: `scale(${this.getZoomScale()}, ${this.getZoomScale()}) `,
        overflowY: "auto",
        boxShadow: "none",
        width: this.getDesignFrameWidthReport(),
        minWidth: this.getDesignFrameWidthReport(),
        maxWidth: this.getDesignFrameWidthReport(),
        height: this.getDesignFrameHeight()
      };
    }
    return {
      transform: `scale(${this.getZoomScale()}, ${this.getZoomScale()}) `,
      overflowY: "auto",
      width: this.getDesignFrameWidth(),
      height: this.getDesignFrameHeight()
    };
  };

  render() {
    let selectedDevice: IDevice = this.deviceList[0];
    if (this.props && this.props.sd && this.props.sd.selectedDevice) {
      selectedDevice = this.props.sd.selectedDevice;
      KMainFunctions.selectedDeviceType(selectedDevice, this.props.sd.dashboardState);
    }
    return (
      <ReactResizeDetector handleWidth handleHeight onResize={this.waitAndResize}>
        <div
          style={this.getFrameStyles() as React.CSSProperties}
          className={
            this.props.sd.dashboardState === DashboardState.design ||
            this.props.sd.dashboardState === DashboardState.reportDesigner
              ? "kuika_iframe_canvas kuika_iframe_design-design-mode"
              : selectedDevice.css === "" || selectedDevice.css === "none"
                ? "kuika_iframe_canvas kuika_iframe_preview-without-device-mode"
                : "kuika_iframe_canvas"
          }
        ></div>
      </ReactResizeDetector>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.Loading) {
      return true;
    }
    // A new paper size arrives as a project change, and this frame is sized from it.
    return changeReason === ESdChangeReason.ChangeProjectStyle;
  };
}

const canvasFrame = withSD<ICanvasFrameProps>(CanvasFrame);
export { canvasFrame as CanvasFrame };
